<script lang="ts" setup>
import { useDisplay } from 'vuetify'
import { computed } from 'vue'
import type { PropType } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import PermissionGate from './PermissionGate.vue'
import UnathorizedState from './UnauthorizedState.vue'

interface Breadcrumb {
  title: string
  to?: RouteLocationRaw
}

interface TabItem {
  label: string
  routeName: string
  disabled?: boolean
}

const skeletonPresets = [
  'table',
  'article',
  'list-item@5',
  'image, text',
] as const
type SkeletonPreset = (typeof skeletonPresets)[number]

const props = defineProps({
  items: { type: Array as PropType<Breadcrumb[]>, required: true },
  mobileFab: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  skeletonType: {
    type: String as PropType<SkeletonPreset | string>,
    default: 'table',
  },
  minContentHeight: { type: [Number, String], default: 0 },
  containerMinWidth: { type: Number, default: 960 },
  containerMaxWidth: { type: Number, default: 1968 },
  required: {
    type: [String, Array] as PropType<string | string[]>,
    default: undefined,
  },
  mode: { type: String as PropType<'any' | 'all'>, default: 'any' },
  customFields: { type: Boolean, default: false },
  tabTitles: {
    type: Array as PropType<TabItem[]>,
    required: false,
    default: undefined,
  },
  bgWhite: { type: Boolean, default: true },
  maxWidth: { type: String, default: '1920px' },
  selectField: { type: Boolean, default: false },
  border: { type: Boolean, default: true, required: false },
})

const emit = defineEmits<{ (e: 'fab-button'): void }>()
const router = useRouter()
const route = useRoute()
const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)
const previousItem = computed(() => props.items[props.items.length - 2])
const currentItem = computed(() => props.items[props.items.length - 1])

const tabIndex = computed(() =>
  props.tabTitles?.findIndex((tab) => tab.routeName === route.name)
)

function navigateToTab(value: unknown) {
  if (!props.tabTitles) return
  const index = Number(value)
  const tab = props.tabTitles[index]
  if (tab && !tab.disabled) {
    router.push({ name: tab.routeName, params: { id: route.params.id } })
  }
}
const contentBgClass = computed(() =>
  props.bgWhite ? 'bg-white' : 'bg-fafafa'
)
</script>

<template>
  <v-app>
    <v-main
      class="layout-main"
      style="margin-top: 64px; min-height: calc(100vh - 64px)"
    >
      <PermissionGate :required="required" :mode="mode">
        <!-- fallback não autorizado -->
        <template #fallback>
          <UnathorizedState
            permission="INVITE_READ"
            cta-label="Pedir acesso"
            @cta="() => $router.push({ name: 'profile' })"
          />
        </template>

        <!-- HEADER -->
        <v-sheet
          class="d-flex align-center border-b-thin bg-white"
          :class="[
            isMobile ? 'px-0' : '',
            !isMobile && !customFields ? 'px-4' : '',
          ]"
          :height="48"
          color="white"
        >
          <div
            class="d-flex w-100 h-100 align-center"
            style="background-color: white"
          >
            <!-- MOBILE -->
            <template v-if="isMobile && !customFields">
              <div class="d-flex justify-space-between align-center w-100">
                <div class="d-flex align-center min-w-0 flex-1">
                  <v-btn
                    icon="mdi-arrow-left"
                    variant="plain"
                    aria-label="Voltar"
                    @click="
                      previousItem?.to
                        ? router.push(previousItem.to)
                        : router.back()
                    "
                  />
                  <h2
                    class="text-subtitle-1 font-weight-medium title-ellipsis m-0"
                  >
                    {{ currentItem?.title ?? '' }}
                  </h2>
                </div>
                <div class="d-flex align-center ml-2">
                  <slot v-if="$slots['publish-button']" name="publish-button" />
                  <slot v-else name="headerAction" />
                </div>
              </div>
            </template>

            <!-- DESKTOP -->
            <template v-else>
              <v-breadcrumbs
                v-if="!customFields"
                :items="items"
                divider=">"
                class="eve-breadcrumbs text-grey-darken-1"
              >
                <template #title="{ item, index }">
                  <router-link
                    v-if="item.to && index !== items.length - 1"
                    :to="item.to"
                    class="text-grey-darken-1 text-decoration-none"
                  >
                    {{ item.title }}
                  </router-link>
                  <span v-else class="green-breadcrumbs">
                    {{ item.title }}
                  </span>
                </template>
              </v-breadcrumbs>
              <v-tabs
                v-else
                v-model="tabIndex"
                background-color="white"
                show-arrows
                class="eve-tabs border-b"
                style="border-color: #d5d5d5"
                slider-color="var(--standart-theme---EVE-primary, #8DC63F)"
                @update:model-value="navigateToTab"
              >
                <v-tab
                  v-for="tab in tabTitles"
                  :id="`tab-${tab.routeName}`"
                  :key="tab.routeName"
                  :disabled="tab.disabled"
                  class="eve-tab"
                  :class="{ 'is-disabled': tab.disabled }"
                >
                  {{ tab.label }}
                </v-tab>
              </v-tabs>
              <div class="d-flex align-center ml-auto">
                <slot v-if="$slots['publish-button']" name="publish-button" />
                <slot v-else name="action" />
              </div>
            </template>
          </div>
        </v-sheet>

        <!-- CONTEÚDO -->
        <v-container
          fluid
          class="d-flex flex-column align-center flex-grow-1"
          :class="
            isMobile
              ? customFields
                ? 'gutters-mobile-zero bg-white'
                : 'gutters-mobile bg-white'
              : 'gutters-desktop bg-fafafa'
          "
        >
          <v-sheet
            class="w-100 h-100 flex-grow-1"
            :style="{
              padding: isMobile ? '0' : selectField ? '0' : '24px',
              border: props.border ? '1px solid #E0E0E0' : '',
              borderRadius: isMobile ? '' : '16px',
            }"
            :class="[contentBgClass]"
            :max-width="!isMobile && maxWidth ? maxWidth : ''"
          >
            <transition name="fade-fast" mode="out-in">
              <div
                :key="loading ? 'skeleton' : 'content'"
                :role="loading ? 'status' : undefined"
                :aria-busy="loading ? 'true' : 'false'"
                aria-live="polite"
              >
                <template v-if="loading">
                  <slot name="skeleton" :type="skeletonType">
                    <v-skeleton-loader
                      :type="skeletonType"
                      class="rounded-lg"
                      boilerplate
                      data-test="baselayout-skeleton"
                    />
                  </slot>
                </template>
                <template v-else>
                  <div data-test="baselayout-content">
                    <slot />
                  </div>
                </template>
              </div>
            </transition>
          </v-sheet>

          <!-- FAB mobile -->
          <v-btn
            v-if="mobileFab && isMobile"
            icon="mdi-plus"
            color="primary"
            variant="flat"
            class="position-fixed z-2 elevation-2 bottom-14 right-10 text-white"
            aria-label="Criar"
            @click="emit('fab-button')"
          />
        </v-container>
      </PermissionGate>
    </v-main>
  </v-app>
</template>

<style scoped>
/* --- BASE SHEET --- */
:deep(.v-sheet) {
  overflow: visible;
}

.layout-main {
  background-color: #fafafa !important;
}

:deep(.v-main__wrap) {
  background-color: #fafafa !important;
}

/* título mobile */
.title-ellipsis {
  overflow: hidden;
  white-space: nowrap;
  max-width: 80vw;
}

/* Gutters condicionais */
.gutters-desktop {
  padding-left: 24px !important;
  padding-right: 24px !important;
  padding-top: 24px !important;
  padding-bottom: 24px !important;
}
.gutters-mobile {
  padding-left: 16px !important;
  padding-right: 16px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* helpers */
.flex-1 {
  flex: 1 1 auto;
}
.min-w-0 {
  min-width: 0;
}

/* transição */
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.12s ease;
}
.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}
</style>
<style>
/* ============================================================= */
/* =====================  TABS (EVE) GLOBAIS  ================== */
/* ============================================================= */

.eve-tabs {
  --v-tabs-height: 48px;
  font-family: 'Open Sans', sans-serif;
  background-color: #fff !important;
  border-bottom: 1px solid #d5d5d5;
}

/* layout base das tabs (todas) */
.eve-tabs .v-tab {
  display: flex !important;
  height: 48px !important;
  padding: 10px 16px !important;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  align-self: stretch;
}

/* fundo da faixa de tabs */
.eve-tabs .v-slide-group__content,
.eve-tabs .v-slide-group__wrapper,
.eve-tabs .v-slide-group {
  background-color: #fff !important;
}

/* estado base (não selecionado) */
.eve-tabs .v-tab .v-btn__content {
  color: #5e5e5e !important;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color 0.2s ease;
}

/* estado selecionado – normal e hover sempre verde */
.eve-tabs .v-tab.v-tab--selected .v-btn__content,
.eve-tabs .v-tab.v-tab--selected:hover .v-btn__content {
  color: var(--standart-theme---EVE-primary, #8dc63f) !important;
  font-weight: 700 !important;
}

/* slider */
.eve-tabs .v-tab__slider {
  height: 3px;
  border-radius: 2px;
  background-color: var(--standart-theme---EVE-primary, #8dc63f) !important;
  transition: all 0.25s ease;
}

/* desabilitada */
.eve-tabs .v-tab.v-tab--disabled .v-btn__content,
.eve-tab.is-disabled .v-btn__content {
  color: rgba(0, 0, 0, 0.37) !important;
  opacity: 1 !important;
}

/* remove ripple/::before padrão */
.eve-tabs .v-tab::before {
  display: none !important;
}

/* hover do tab selecionado */
.eve-tabs .v-tab.v-tab--selected:hover {
  background-color: #f2f8e8 !important;
}

/* hover das não selecionadas */
.eve-tabs .v-tab:not(.v-tab--selected):hover .v-btn__overlay {
  opacity: 1 !important;
}

/* overlay base */
.eve-tabs .v-tab .v-btn__overlay {
  transition: background-color 0.2s ease;
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* overlay do selecionado desativado */
.eve-tabs .v-tab.v-tab--selected .v-btn__overlay {
  background-color: transparent !important;
  opacity: 0 !important;
}

.green-breadcrumbs {
  color: var(--standart-theme---EVE-primary, #8dc63f);
}

.eve-breadcrumbs .v-breadcrumbs-item--disabled {
  opacity: 1 !important;
}

.eve-breadcrumbs .green-breadcrumbs {
  color: var(--standart-theme---EVE-primary, #8dc63f) !important;
}

.gutters-mobile-zero {
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.bg-fafafa {
  background-color: #fafafa !important;
}
</style>
